import { STRINGS } from 'config';

/**
 *
 * Using BEM design pattern detailed here: https://tech.decisiv.com/structuring-our-styled-components-part-i-2bf21fa64b28
 *
 * BEM definition can be found here: http://getbem.com/
 *
 */

const { TRIAGE_LEVEL } = STRINGS;

const screenXXs = 480;
const screenXs = 550;
const screenSm = 768;
const screenMd = 1025;
const screenLg = 1200;
const screenXl = 1500;

const xsGridMargin = 10;
const smGridMargin = 20;
const mdGridMargin = 25;
const lgGridMargin = 50;
const screenLgWidth = screenLg - 2 * lgGridMargin;

export const screenSizes = {
  screenXXs: screenXXs,
  screenXs: screenXs,
  screenSm: screenSm,
  screenMd: screenMd,
  screenLg: screenLg,
  screenXl: screenXl,
  screenXXsMax: screenXs - 1,
  screenXsMax: screenSm - 1,
  screenSmMax: screenMd - 1,
  screenMdMax: screenLg - 1,
  screenLgMax: screenXl - 1,
  screenLgWidth: screenLgWidth,
  lgGridMargin: lgGridMargin,
  mdGridMargin: mdGridMargin,
  smGridMargin: smGridMargin,
  xsGridMargin: xsGridMargin,
};

/** Font sizes */
const fontSizeSmall = '12px';
const fontSizePrimary = '16px';
const fontSizeMedium = '20px';
const fontSizeLarge = '22px';
const fontSizeXLarge = '32px';
const fontSizeXXLarge = '40px';

/** Font family */
const fontFamilyPrimary = 'Arial, sans-serif';
const fontFamilySecondary = '"OpenSans", sans-serif';
const fontFamilySecondaryLight = '"OpenSansLight", sans-serif';
const fontFamilyButton = '"Trebuchet MS", sans-serif';

/** Font colors */
const fontColorShadeOne = '#4d4d4d';
const fontColorShadeTwo = '#6c6c6c';
const fontColorShadeThree = '#FFFFFF';

/** Branding colors */
const brandingPrimaryShadeOne = '#00648C';
const brandingPrimaryShadeTwo = '#3A3C49';
const brandingPrimaryShadeThree = '#0073A2';
const brandingPrimaryShadeFour = '#0080b5'; // eConsultDarkBlue and as rgba 'rgba(0, 128, 181, 1)'
const brandingPrimaryShadeFive = '#379BCF';
const brandingPrimaryShade = '#9ed7f1'; // rgba(46, 174, 226, 0.5)
const brandingPrimaryShadeSix = '#CCE0E8';
const brandingPrimaryShadeSeven = '#d4e5f0';
const brandingPrimaryShadeEight = '#e8f2f7'; // 'rgba(0, 128, 181, 0.1)' eConsultLightBlue which is same as dark but with lower opacity
const brandingPrimaryShadeNine = '#005FB8';
const brandingPrimaryShadeTen = '#1E194B';

const brandingSecondaryShadeOne = '#F55061'; // eConsultPink
const brandingSecondaryShadeTwo = '#007DA3';

/** Other colors */
const completeColor = '#0A8F19';
const confirmColor = '#6D7B27';
const suicideColor = '#4a4a4a';
const disabledColor = '#86858f';
const disabledFontColor = '#c3c2c8';
const disabledFontColorShadeTwo = 'gray';
const borderColor = '#d2d2d2';
const borderColorTwo = '#8F8CA5';
const inputBorderColor = '#4B476F';
const dropZoneColor = '#E1F6FE';
const infoColor = '#f5f1c2'; // Pale yellow
const infoAlertColor = '#faf8c8'; // Paler yellow
const alertColor = '#E98907'; // Bright Orange
const nonUrgentColor = '#C25400'; // Dull orange
const invalidColor = '#cf442f'; // Dark Red
const invalidColorShadeTwo = '#af000a'; // Crimson
const urgentColor = invalidColor; // Dark Red
const emergencyColor = '#E11418'; // Bright Red

const forceScrollBarStylesPC = `
  scrollbar-width: thin;
  
  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 5px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0,0,0,.5);
    -webkit-box-shadow: 0 0 1px rgba(255,255,255,.5);
  }
  ::-webkit-scrollbar-track {
    background: transparent
  }
`;
const scrollBarStylesETriage = `
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 15px;
    border: 1px solid white;
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: rgba(255, 255, 255, 0.8);
  }
`;

const clinicalReviewStyles = {
  [TRIAGE_LEVEL.NORMAL]: 'color: #29c229',
  [TRIAGE_LEVEL.GP_CAUTION]: 'color: orange',
  [TRIAGE_LEVEL.RED_FLAG]: 'color: orange',
  [TRIAGE_LEVEL.URGENT]: 'color: #ff7c69',
  [TRIAGE_LEVEL.ADVICE_URGENT]: 'color: #ff7c69',
  [TRIAGE_LEVEL.ADVICE_URGENT_MH]: 'color: #ff7c69',
  [TRIAGE_LEVEL.EMERGENCY]: 'color: #ff7c69',
  [TRIAGE_LEVEL.ADVICE_EMERGENCY]: 'color: #ff7c69',
  [TRIAGE_LEVEL.ADVICE_EMERGENCY_MH]: 'color: #ff7c69',
  [TRIAGE_LEVEL.SUICIDAL]: '',
  [TRIAGE_LEVEL.ADVICE_DENTAL]: 'color: #ff7c69',
  [TRIAGE_LEVEL.ADVICE_REDIRECT]: 'color: #ff7c69',
  [TRIAGE_LEVEL.REDIRECT]: 'color: #ff7c69',
  [TRIAGE_LEVEL.REDIRECT_HMRC]: 'color: #ff7c69',
  [TRIAGE_LEVEL.INELIGIBLE]: 'color: #ff7c69',
  [TRIAGE_LEVEL.CV19_EMERGENCY]: 'color: #ff7c69',
  groupScore: 'color: blue',
};

const eTriageClinicalReviewStyles = {
  [TRIAGE_LEVEL.NORMAL]: 'background-image: linear-gradient(-180deg, #0ba601, #088b00 100%);; color: white',
  [TRIAGE_LEVEL.GP_CAUTION]: 'background-image: linear-gradient(-180deg, #fbed50, #CCBD00 100%); color: white',
  [TRIAGE_LEVEL.RED_FLAG]: 'background-image: linear-gradient(-180deg, #fbed50, #CCBD00 100%); color: #d0021b',
  [TRIAGE_LEVEL.URGENT]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.ADVICE_URGENT]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.ADVICE_URGENT_MH]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.EMERGENCY]: 'background-image: linear-gradient(-180deg, #d0021b, #9C000F 100%); color: white',
  [TRIAGE_LEVEL.ADVICE_EMERGENCY]: 'background-image: linear-gradient(-180deg, #d0021b, #9C000F 100%); color: white',
  [TRIAGE_LEVEL.SUICIDAL]: '',
  [TRIAGE_LEVEL.ADVICE_DENTAL]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.ADVICE_REDIRECT]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.REDIRECT]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.REDIRECT_HMRC]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.INELIGIBLE]: 'background-image: linear-gradient(-180deg, #f59e00, #FF6601 100%); color: white',
  [TRIAGE_LEVEL.CV19_EMERGENCY]: 'background-image: linear-gradient(-180deg, #d0021b, #9C000F 100%); color: white',
  groupScore: 'color: blue',
};

const sharedStyles = {
  brandingPrimaryShadeOne: brandingPrimaryShadeOne,
  brandingPrimaryShadeTwo: brandingPrimaryShadeTwo,
  brandingPrimaryShadeThree: brandingPrimaryShadeThree,
  brandingPrimaryShadeFour: brandingPrimaryShadeFour,
  brandingPrimaryShadeFive: brandingPrimaryShadeFive,
  brandingPrimaryShade: brandingPrimaryShade,
  brandingPrimaryShadeSix: brandingPrimaryShadeSix,
  brandingPrimaryShadeSeven: brandingPrimaryShadeSeven,
  brandingPrimaryShadeEight: brandingPrimaryShadeEight,
  brandingSecondaryShadeOne: brandingSecondaryShadeOne,
  brandingSecondaryShadeTwo: brandingSecondaryShadeTwo,
  invalidColor: invalidColor,
  invalidColorShadeTwo: invalidColorShadeTwo,
  borderColor: borderColor,
  infoColor: infoColor,
  infoAlertColor: infoAlertColor,
  alertColor: alertColor,
  completeColor: completeColor,
  confirmColor: confirmColor,
  suicideColor: suicideColor,
  urgentColor: urgentColor,
  nonUrgentColor: nonUrgentColor,
  emergencyColor: emergencyColor,
  clinicalReviewStyles: clinicalReviewStyles,
  disabledColor: disabledColor,
  disabledFontColor: disabledFontColor,
};

export const primaryCareTheme = {
  ...screenSizes,
  ...sharedStyles,

  fontFamily: fontFamilyPrimary,
  fontFamilyLight: fontFamilyPrimary,
  fontFamilyButton: fontFamilyPrimary,
  fontSizeLarge: '19px',
  fontSizePrimary: fontSizePrimary,
  fontSizeSmall: fontSizeSmall,
  fontColorPrimary: fontColorShadeOne,
  smallFontColor: fontColorShadeTwo,
  inputBorders: `2px solid ${brandingPrimaryShadeFour}`,
  paddingPercentageHorizontalLargeScreen: 12,
  forceScrollBarStyles: forceScrollBarStylesPC,
  helpLabelFontSize: fontSizePrimary,
  tooltipBorder: '2px solid gray',
  dropZoneBorder: '3px dashed rgba(46, 174, 226, 0.5)',
  dropZoneBackground: 'rgba(0,128,181,0.2)',
  dropZoneBackgroundHighlight: brandingPrimaryShadeEight,
  footerPadding: '10px 45px 10px 12%',
  prevCaretFill: brandingPrimaryShadeOne,
  prevCaretSize: '20px',
  labelColor: brandingPrimaryShadeOne,

  headingColorPrimary: brandingPrimaryShadeOne,
  backgroundColorPrimary: 'white',

  errorMessagePadding: '5px 15px',
  errorMessageFontSize: fontSizePrimary,
  errorMessageLineHeight: 1.5,
  errorMessageBorderRadius: '5px',

  buttonPrimaryFontColorActive: 'white',
  buttonPrimaryBackgroundColor: brandingPrimaryShadeThree,
  buttonPrimaryBackgroundColorActive: brandingPrimaryShadeTwo,
  buttonPrimaryBorderColor: brandingPrimaryShadeThree,
  buttonPrimaryBorder: `solid 2px ${brandingPrimaryShadeThree}`,
  buttonPrimaryBorderColorActive: brandingPrimaryShadeTwo,
  buttonPrimaryBorderActive: `solid 2px ${brandingPrimaryShadeTwo}`,
  buttonPrimaryBorderRadius: '15px',
  buttonPrimaryBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  buttonPrimaryDisabledBackground: disabledColor,
  buttonPrimaryDisabledBorder: '0',
  buttonPrimaryDisabledBoxShadow: 'none',
  buttonPaddingSmall: '14px 30px',

  buttonSecondaryMargin: '0.6em 0',
  buttonSecondaryPadding: '5px 15px',
  buttonSecondaryFontSize: fontSizePrimary,
  buttonSecondaryFontFamily: fontFamilyButton,
  buttonSecondaryColor: brandingPrimaryShadeThree,
  buttonSecondaryBackgroundColor: 'white',
  buttonSecondaryBorder: `1px solid ${brandingPrimaryShadeThree}`,
  buttonSecondaryBorderRadius: '8px',

  checkboxButtonPrimaryFontColorActive: fontColorShadeOne,
  checkboxButtonPrimaryBackgroundColor: 'white',
  checkboxButtonPrimaryBackgroundColorActive: 'white',
  checkboxButtonPrimaryBorder: `solid 2px ${brandingPrimaryShadeThree}`,
  checkboxButtonPrimaryBorderGray: `solid 2px ${borderColor}`,
  checkboxButtonPrimaryBorderActive: `solid 2px ${confirmColor}`,
  checkboxButtonPrimaryBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  checkboxButtonPrimaryBoxShadowActive: 'none',
  checkboxButtonPrimaryCheckboxDisplay: 'block',

  selectOptionBoxShadowInactiveFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  selectOptionBoxShadowActive: 'none',
  selectOptionBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  selectOptionWidthTwoOrFewer: '100%',
  selectOptionWidthThreeOrMore: '100%',
  selectOptionMarginRightThreeOrMore: '0',
  selectOptionFlexDirection: 'row',
  selectOptionGroupFlexDirection: 'column',
  selectOptionGroupFlexWrap: 'nowrap',
  selectOptionMinHeight: 'auto',
};

export const newExperiencePrimaryCareTheme = {
  ...screenSizes,
  ...sharedStyles,

  fontFamily: fontFamilyPrimary,
  fontFamilyLight: fontFamilyPrimary,
  fontFamilyButton: fontFamilyButton,
  fontSizeLarge: '19px',
  fontSizePrimary: fontSizePrimary,
  fontSizeSmall: fontSizeSmall,
  fontColorPrimary: fontColorShadeOne,
  smallFontColor: fontColorShadeTwo,
  inputBorders: `1px solid ${inputBorderColor}`,
  textareaBorderRadius: '4px',
  paddingPercentageHorizontalLargeScreen: 12,
  forceScrollBarStyles: forceScrollBarStylesPC,
  helpLabelFontSize: fontSizePrimary,
  tooltipBorder: '1px solid gray',
  newExperiencePrimary: brandingPrimaryShadeTen,
  anchorColor: brandingPrimaryShadeTen,
  anchorTextDecoration: 'underline',
  dropZoneBorder: '2px dashed #83DBFB',
  dropZoneBackground: dropZoneColor,
  dropZoneBackgroundHighlight: brandingPrimaryShadeEight,
  footerPadding: '10px 45px 0px 12%',
  headingColorPrimary: brandingPrimaryShadeOne,
  backgroundColorPrimary: 'white',
  prevCaretFill: borderColorTwo,
  prevCaretTransform: 'rotate(90deg)',
  prevCaretSize: '10px',
  labelColor: brandingPrimaryShadeTen,

  errorMessagePadding: '5px 15px',
  errorMessageFontSize: fontSizePrimary,
  errorMessageLineHeight: 1.5,
  errorMessageBorderRadius: '5px',

  buttonPrimaryFontColorActive: 'white',
  buttonPrimaryBackgroundColor: brandingPrimaryShadeNine,
  buttonPrimaryBackgroundColorActive: brandingPrimaryShadeTen,
  buttonPrimaryBorderColor: brandingPrimaryShadeThree,
  buttonPrimaryBorder: `solid 1px ${brandingPrimaryShadeThree}`,
  buttonPrimaryBorderRadius: '8px',
  buttonPrimaryBorderColorActive: brandingPrimaryShadeTen,
  buttonPrimaryBorderActive: `solid 1px ${brandingPrimaryShadeTwo}`,
  buttonPrimaryBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  buttonPrimaryDisabledBackground: disabledColor,
  buttonPrimaryDisabledBorder: '0',
  buttonPrimaryDisabledBoxShadow: 'none',
  buttonPadding: '5px 15px 5px 0',
  buttonPaddingSmall: '10px 0',

  buttonSecondaryMargin: '0.6em 0',
  buttonSecondaryPadding: '5px 15px',
  buttonSecondaryFontSize: fontSizePrimary,
  buttonSecondaryFontFamily: fontFamilyButton,
  buttonSecondaryColor: brandingPrimaryShadeNine,
  buttonSecondaryBackgroundColor: 'white',
  buttonSecondaryBorder: `1px solid ${brandingPrimaryShadeNine}`,
  buttonSecondaryBorderRadius: '8px',

  checkboxButtonPrimaryFontColorActive: fontColorShadeOne,
  checkboxButtonPrimaryBackgroundColor: 'white',
  checkboxButtonPrimaryBackgroundColorActive: 'white',
  checkboxButtonPrimaryBorder: `solid 1px ${borderColorTwo}`,
  checkboxButtonPrimaryBorderActive: `solid 1px ${confirmColor}`,
  checkboxButtonPrimaryBorderGray: `solid 2px ${borderColor}`,
  checkboxButtonPrimaryBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  checkboxButtonPrimaryBoxShadowActive: 'none',
  checkboxButtonPrimaryCheckboxDisplay: 'block',

  selectOptionBoxShadowInactiveFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  selectOptionBoxShadowActive: 'none',
  selectOptionBoxShadowFocus: `0 0px 1px 2px white, 0 0px 4px 4px ${brandingPrimaryShadeThree}`,
  selectOptionWidthTwoOrFewer: '100%',
  selectOptionWidthThreeOrMore: '100%',
  selectOptionMarginRightThreeOrMore: '0',
  selectOptionFlexDirection: 'row',
  selectOptionGroupFlexDirection: 'column',
  selectOptionGroupFlexWrap: 'nowrap',
  selectOptionMinHeight: 'auto',
};

export const eTriageTheme = {
  ...screenSizes,
  ...sharedStyles,

  invalidColor: invalidColorShadeTwo,
  disabledFontColor: disabledFontColorShadeTwo,
  clinicalReviewStyles: eTriageClinicalReviewStyles,
  fontFamily: fontFamilySecondary,
  fontFamilyLight: fontFamilySecondaryLight,
  fontSizeLarge: fontSizeXXLarge,
  fontSizePrimary: fontSizeXLarge,
  fontSizeMedium: fontSizeLarge,
  fontSizeSmall: fontSizeMedium,
  fontColorPrimary: fontColorShadeThree,
  smallFontColor: fontColorShadeThree,
  inputBorders: `1px solid ${brandingPrimaryShadeFour}`,
  paddingPercentageHorizontalLargeScreen: 2,
  forceScrollBarStyles: scrollBarStylesETriage,
  helpLabelFontSize: fontSizeMedium,

  headingColorPrimary: fontColorShadeThree,
  backgroundColorPrimary: brandingPrimaryShadeOne,

  errorMessagePadding: '30px',
  errorMessageFontSize: fontSizeMedium,
  errorMessageLineHeight: 1,
  errorMessageBorderRadius: '0',

  buttonPrimaryFontColorActive: brandingSecondaryShadeTwo,
  buttonPrimaryBackgroundColor: brandingSecondaryShadeOne,
  buttonPrimaryBackgroundColorActive: brandingPrimaryShadeSix,
  buttonPrimaryBorderColor: brandingSecondaryShadeOne,
  buttonPrimaryBorder: 'none',
  buttonPrimaryBorderRadius: '50px',
  buttonPrimaryBorderColorActive: brandingPrimaryShadeSix,
  buttonPrimaryBorderActive: 'none',
  buttonPrimaryBoxShadowFocus: 'none',
  buttonPrimaryDisabledBackground: 'linear-gradient(-180deg, #c5c5c5 0%, #929292 100%)',
  buttonPrimaryDisabledBorder: '3px solid gray',
  buttonPrimaryDisabledBoxShadow: '2px 2px 4px 0 rgba(0, 0, 0, 0.5)',

  buttonSecondaryMargin: '0',
  buttonSecondaryPadding: '10px 40px',
  buttonSecondaryFontSize: fontSizeLarge,
  buttonSecondaryFontFamily: 'inherit',
  buttonSecondaryColor: 'white',
  buttonSecondaryBackgroundColor: 'transparent',
  buttonSecondaryBorder: '1px solid white',
  buttonSecondaryBorderRadius: '50px',

  checkboxButtonPrimaryFontColorActive: brandingSecondaryShadeTwo,
  checkboxButtonPrimaryBackgroundColor: brandingPrimaryShadeOne,
  checkboxButtonPrimaryBackgroundColorActive: brandingPrimaryShadeSix,
  checkboxButtonPrimaryBorder: 'solid 2px white',
  checkboxButtonPrimaryBorderColorActive: `solid 2px ${brandingPrimaryShadeOne}`,
  checkboxButtonPrimaryBoxShadowFocus: '',
  checkboxButtonPrimaryBoxShadowActive: 'inset 0px 0px 17px 1px #009cdc',
  checkboxButtonPrimaryCheckboxDisplay: 'none',

  selectOptionBoxShadowInactiveFocus: 'none',
  selectOptionBoxShadowActive: 'inset 0px 0px 17px 1px #009cdc',
  selectOptionBoxShadowFocus: 'inset 0px 0px 17px 1px #009cdc',
  selectOptionSecondaryWidthPrimary: '100%',
  selectOptionSecondaryWidthSecondary: '49%',
  selectOptionWidthTwoOrFewer: '49%',
  selectOptionWidthThreeOrMore: '31%',
  selectOptionMarginRightThreeOrMore: '3%',
  selectOptionFlexDirection: 'column',
  selectOptionMinHeight: '100px',
  selectOptionGroupFlexDirection: 'row',
  selectOptionGroupFlexWrap: 'wrap',
  additionalNextButtonStyles: 'align-self: flex-start; height: 100px; padding: 0 40px; font-size: 40px',
};
